import { useState } from 'react';

interface Task {
  id: number;
  name: string;
  description: string;
  owner: string;
  deadline: string;
  status: string;
  priority: string;
  createdDate: string;
}

interface TaskListProps {
  tasks: Task[];
  onDelete: (id: number) => void;
  onUpdate: (task: Task) => void;
}

export default function TaskList({ tasks, onDelete, onUpdate }: TaskListProps) {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-600 text-white';
      case 'High':
        return 'bg-red-100 text-red-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'On Hold':
        return 'bg-orange-100 text-orange-700';
      case 'Pending':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch = (task.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (task.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (task.owner || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'All' || task.status === filterStatus;
    const matchesPriority = filterPriority === 'All' || task.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      onDelete(id);
      if (selectedTask?.id === id) {
        setSelectedTask(null);
      }
    }
  };

  const handleStatusChange = (task: Task, newStatus: string) => {
    const updatedTask = { ...task, status: newStatus };
    onUpdate(updatedTask);
    if (selectedTask?.id === task.id) {
      setSelectedTask(updatedTask);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="All">All Status</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="On Hold">On Hold</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="All">All Priority</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredTasks.length === 0 ? (
              <div className="p-12 text-center">
                <i className="ri-task-line text-5xl text-gray-300 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
                <p className="text-gray-500 text-sm">No tasks found</p>
              </div>
            ) : (
              filteredTasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => setSelectedTask(task)}
                  className={`p-5 hover:bg-gray-50 transition-colors cursor-pointer ${
                    selectedTask?.id === task.id ? 'bg-teal-50 border-l-4 border-l-teal-600' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{task.name}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2">{task.description}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                        <span className={`px-2 py-1 rounded font-medium ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line w-3 h-3 flex items-center justify-center"></i>
                          {task.owner}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                          {task.deadline}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(task.id);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-lg w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="lg:col-span-1">
        {selectedTask ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Task Details</h3>
              <button
                onClick={() => setSelectedTask(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">TASK NAME</label>
                <p className="text-sm font-semibold text-gray-900">{selectedTask.name}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">DESCRIPTION</label>
                <p className="text-sm text-gray-700 leading-relaxed">{selectedTask.description}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">OWNER</label>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {selectedTask.owner.charAt(0)}
                  </div>
                  <span className="text-sm font-medium text-gray-900">{selectedTask.owner}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2">PRIORITY</label>
                  <span className={`inline-block px-3 py-1.5 rounded text-xs font-medium ${getPriorityColor(selectedTask.priority)}`}>
                    {selectedTask.priority}
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2">STATUS</label>
                  <select
                    value={selectedTask.status}
                    onChange={(e) => handleStatusChange(selectedTask, e.target.value)}
                    className={`w-full px-3 py-1.5 rounded text-xs font-medium cursor-pointer border-0 ${getStatusColor(selectedTask.status)}`}
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">DEADLINE</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <i className="ri-calendar-event-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                  {selectedTask.deadline}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">CREATED DATE</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <i className="ri-calendar-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                  {selectedTask.createdDate}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center sticky top-6">
            <i className="ri-file-list-line text-5xl text-gray-300 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
            <p className="text-sm text-gray-500">Select a task to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}
